import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { EntitlementsProvider } from "./contexts/EntitlementsContext";
import FeatureGate from "./components/FeatureGate";

import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import DataCollection from "./pages/DataCollection";
import Materiality from "./pages/Materiality";
import Emissions from "./pages/Emissions";
import Anomalies from "./pages/Anomalies";
import Reports from "./pages/Reports";
import ReportDetail from "./pages/ReportDetail";
import Settings from "./pages/Settings";
import PlatformHealth from "./pages/platform/PlatformHealth";
import PlatformTenants from "./pages/platform/PlatformTenants";
import PlatformAnnouncements from "./pages/platform/PlatformAnnouncements";
import PlatformAudit from "./pages/platform/PlatformAudit";
import { PlatformBillingPage, PlatformPlansPage, PlatformInvoicesPage, PlatformTrainingPage } from "./pages/platform/PlatformBillingPages";
import PlatformAccounting from "./pages/platform/PlatformAccounting";
import Scope3 from "./pages/Scope3";
import Scope3Bridge from "./pages/Scope3Bridge";
import SocialGov from "./pages/SocialGov";
import Scenarios from "./pages/Scenarios";
import ClimateOperations from "./pages/ClimateOperations";
import DataFlowMap from "./pages/DataFlowMap";
import RiskRegister from "./pages/RiskRegister";
import ESGScorecard from "./pages/ESGScorecard";
import Connectors from "./pages/Connectors";
import Filings from "./pages/Filings";
import Documents from "./pages/Documents";
import CustomKPIs from "./pages/CustomKPIs";
import ScoreIntelligence from "./pages/ScoreIntelligence";
import SustainabilityReport from "./pages/SustainabilityReport";
import ReportStudio from "./pages/ReportStudio";
import SuppliersHub from "./pages/SuppliersHub";
import SupplierResponse from "./pages/SupplierResponse";
import Reconciliation from "./pages/Reconciliation";
import ProjectGallery from "./pages/ProjectGallery";
import PublicGallery from "./pages/PublicGallery";
import SelfServiceOnboarding from "./pages/SelfServiceOnboarding";
import SelfServiceProvisioning from "./pages/SelfServiceProvisioning";
import OnboardingDashboard from "./pages/OnboardingDashboard";
import Landing from "./pages/Landing";
import MfaEnrollGate from "./components/MfaEnrollGate";
import VerifiedBadge from "./pages/VerifiedBadge";
import VerifiedPage from "./pages/VerifiedPage";
import GreenFinancing from "./pages/GreenFinancing";
import Environment from "./pages/Environment";
import MobileCombustion from "./pages/MobileCombustion";
import OperationalEfficiency from "./pages/OperationalEfficiency";
import CBAM from "./pages/CBAM";
import { ReportingLocal, ReportingInternational } from "./pages/Reporting";
import CustomerCompliance from "./pages/CustomerCompliance";
import FinanceOffset from "./pages/FinanceOffset";
import DataManagement from "./pages/DataManagement";
import QaFindings from "./pages/QaFindings";
import AITransparency from "./pages/AITransparency";
import Initiatives from "./pages/Initiatives";
import Readiness from "./pages/Readiness";
import BaselineWizard from "./pages/BaselineWizard";
import UserGuide from "./pages/UserGuide";
import GreenFinancingGuide from "./pages/GreenFinancingGuide";
import WhyLestari from "./pages/WhyLestari";
import About from "./pages/About";
import Billing from "./pages/Billing";
import ServicesAdmin from "./pages/platform/ServicesAdmin";
import ValuePrint from "./pages/ValuePrint";
import HazardRisk from "./pages/HazardRisk";
import GHGInventory from "./pages/GHGInventory";
import PlatformTemplates from "./pages/platform/PlatformTemplates";
import PlatformHazards from "./pages/platform/PlatformHazards";
import PlatformGHGCatalog from "./pages/platform/PlatformGHGCatalog";
import PlatformEmissionFactors from "./pages/platform/PlatformEmissionFactors";
import PlatformLocaleMaster from "./pages/platform/PlatformLocaleMaster";
import PlatformSingpass from "./pages/platform/PlatformSingpass";
import SingpassCallback from "./pages/SingpassCallback";
import PlatformMedia from "./pages/platform/PlatformMedia";
import PlatformDeliverability from "./pages/platform/PlatformDeliverability";

function ProtectedShell() {
  const { user, tenant, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background text-muted-foreground font-mono text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/welcome" replace />;
  if (user.mfa_enrollment_required) return <MfaEnrollGate />;
  // A signed-in user with no tenant yet is mid Phase-2 provisioning — send them there.
  if (user.role !== "super_admin" && !user.tenant_id && !tenant) {
    return <Navigate to="/provision" replace />;
  }
  // Super admin operates the platform console only — never tenant modules
  if (user.role === "super_admin" && !pathname.startsWith("/admin")) {
    return <Navigate to="/admin/health" replace />;
  }
  if (user.role !== "super_admin" && pathname.startsWith("/admin")) {
    return <Navigate to="/" replace />;
  }
  // Super admin bypasses onboarding
  if (user.role !== "super_admin" && tenant && !tenant.onboarded) {
    return <Navigate to="/onboarding" replace />;
  }
  return <EntitlementsProvider><Layout><Outlet /></Layout></EntitlementsProvider>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="bottom-right" richColors />
        <Routes>
          <Route path="/supplier/respond/:token" element={<SupplierResponse />} />
          <Route path="/auth/sgverify/callback" element={<SingpassCallback />} />
          <Route path="/gallery/public/:slug" element={<PublicGallery />} />
          <Route path="/get-started" element={<SelfServiceOnboarding />} />
          <Route path="/provision" element={<SelfServiceProvisioning />} />
          <Route path="/welcome" element={<Landing />} />
          <Route path="/why-lestari" element={<WhyLestari />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Landing />} />
          <Route path="/badge/:slug" element={<VerifiedBadge />} />
          <Route path="/verified/:slug" element={<VerifiedPage />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route element={<ProtectedShell />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/setup" element={<OnboardingDashboard />} />
            <Route path="/data" element={<DataCollection />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/materiality" element={<Materiality />} />
            <Route path="/emissions" element={<Emissions />} />
            <Route path="/anomalies" element={<Anomalies />} />
            <Route path="/scope3" element={<Scope3 />} />
            <Route path="/scope3-bridge" element={<Scope3Bridge />} />
            <Route path="/social-gov" element={<SocialGov />} />
            <Route path="/custom-kpis" element={<CustomKPIs />} />
            <Route path="/score-intelligence" element={<FeatureGate featureKey="score_intelligence"><ScoreIntelligence /></FeatureGate>} />
            <Route path="/suppliers" element={<SuppliersHub />} />
            <Route path="/supplier-insights" element={<Navigate to="/suppliers" replace />} />
            <Route path="/supplier-portal-admin" element={<Navigate to="/suppliers" replace />} />
            <Route path="/gallery" element={<ProjectGallery />} />
            <Route path="/reconciliation" element={<Reconciliation />} />
            <Route path="/scenarios" element={<FeatureGate featureKey="climate_scenarios"><Scenarios /></FeatureGate>} />
            <Route path="/climate-operations" element={<FeatureGate featureKey="climate_scenarios"><ClimateOperations /></FeatureGate>} />
            <Route path="/data-flow" element={<DataFlowMap />} />
            <Route path="/connectors" element={<Connectors />} />
            <Route path="/filings" element={<Filings />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/:id" element={<ReportDetail />} />
            <Route path="/sustainability-report" element={<SustainabilityReport />} />
            <Route path="/report-studio" element={<ReportStudio />} />
            <Route path="/financing" element={<FeatureGate featureKey="green_financing"><GreenFinancing /></FeatureGate>} />
            {/* Redesigned navigation — placeholder modules (gaps / not yet built) */}
            <Route path="/environment" element={<Environment />} />
            <Route path="/mobile-combustion" element={<MobileCombustion />} />
            <Route path="/operational-efficiency" element={<OperationalEfficiency />} />
            <Route path="/reporting/local" element={<ReportingLocal />} />
            <Route path="/reporting/international" element={<ReportingInternational />} />
            <Route path="/reporting/customer-compliance" element={<CustomerCompliance />} />
            <Route path="/reporting/cbam" element={<FeatureGate featureKey="cbam_compliance"><CBAM /></FeatureGate>} />
            <Route path="/risk-register" element={<RiskRegister />} />
            <Route path="/initiatives" element={<Initiatives />} />
            <Route path="/esg-scorecard" element={<ESGScorecard />} />
            <Route path="/finance-offset" element={<FinanceOffset />} />
            <Route path="/icp-offset" element={<Navigate to="/finance-offset" replace />} />
            <Route path="/data-management" element={<DataManagement />} />
            <Route path="/ghg-inventory" element={<GHGInventory />} />
            <Route path="/qa-findings" element={<QaFindings />} />
            <Route path="/facilities-dashboard" element={<Navigate to="/environment?view=facilities" replace />} />
            <Route path="/net-zero-roadmap" element={<Navigate to="/environment?view=roadmap" replace />} />
            <Route path="/baseline" element={<BaselineWizard />} />
            <Route path="/guide" element={<UserGuide />} />
            <Route path="/financing/guide" element={<GreenFinancingGuide />} />
            <Route path="/ai-transparency" element={<AITransparency />} />
            <Route path="/readiness" element={<Readiness />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/valueprint" element={<ValuePrint />} />
            <Route path="/hazard-risk" element={<HazardRisk />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/services" element={<Navigate to="/billing?tab=services-catalogue" replace />} />
            <Route path="/admin/services" element={<ServicesAdmin />} />
            <Route path="/admin/service-engagements" element={<ServicesAdmin />} />
            <Route path="/admin" element={<Navigate to="/admin/health" replace />} />
            <Route path="/admin/health" element={<PlatformHealth />} />
            <Route path="/admin/audit" element={<PlatformAudit />} />
            <Route path="/admin/email" element={<PlatformDeliverability />} />
            <Route path="/admin/tenants" element={<PlatformTenants />} />
            <Route path="/admin/announcements" element={<PlatformAnnouncements />} />
            <Route path="/admin/billing" element={<PlatformBillingPage />} />
            <Route path="/admin/plans" element={<PlatformPlansPage />} />
            <Route path="/admin/invoices" element={<PlatformInvoicesPage />} />
            <Route path="/admin/accounting" element={<PlatformAccounting />} />
            <Route path="/admin/training" element={<PlatformTrainingPage />} />
            <Route path="/admin/templates" element={<PlatformTemplates />} />
            <Route path="/admin/hazards" element={<PlatformHazards />} />
            <Route path="/admin/ghg-catalog" element={<PlatformGHGCatalog />} />
            <Route path="/admin/emission-factors" element={<PlatformEmissionFactors />} />
            <Route path="/admin/locale" element={<PlatformLocaleMaster />} />
            <Route path="/admin/singpass" element={<PlatformSingpass />} />
            <Route path="/admin/media" element={<PlatformMedia />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
